import { EditBankDetailsComponent } from '@accounts/company-account/edit-account/edit-bank-details/edit-bank-details.component';
import { EditSignatoryDetailsComponent } from '@accounts/company-account/edit-account/edit-signatory-details/edit-signatory-details.component';
import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EditJointCustomerInformationComponent } from '../edit-joint-customer-information/edit-joint-customer-information.component';
import { EditJointDocsComponent } from '../edit-joint-docs/edit-joint-docs.component';
import { EditJointEmploymentComponent } from '../edit-joint-employment/edit-joint-employment.component';
import { EditJointKinComponent } from '../edit-joint-kin/edit-joint-kin.component';
import { EditJointLocationComponent } from '../edit-joint-location/edit-joint-location.component';
import { EditJointMeansOfIdComponent } from '../edit-joint-means-of-id/edit-joint-means-of-id.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { BehaviorSubject, map, Observable, Subscription } from 'rxjs';
import {
  GetJointAccountData,
  JointAccountbankinfo,
  JointEmployerkyc,
  JointIdkyc,
  Jointkyc,
  JointLocationkyc,
  JointNextofkin,
  JointPersonaldetails,
} from '@core/models/admin/account/joint';
import { DocKyc } from '@core/models/admin/account';
import {
  CorporateRegistrationService,
  GetAccountSignatoriesData,
} from '@core/services/corporate-registration.service';
import { CommonModule } from '@angular/common';
import { NotificationFacade } from '@core/facades/notification.facade';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { JointRegistrationService } from '@core/services/joint-registration.service';
import { LoadingService } from '@core/services/loading.service';
import { URL_KEYS } from '@core/constants/url-keys.constants';

@Component({
  selector: 'app-edit-joint-details',
  standalone: true,
  imports: [
    EditJointMeansOfIdComponent,
    EditJointCustomerInformationComponent,
    EditJointKinComponent,
    EditJointEmploymentComponent,
    EditBankDetailsComponent,
    EditJointLocationComponent,
    EditJointDocsComponent,
    EditSignatoryDetailsComponent,
    NgxSkeletonLoaderModule,
    CommonModule,
  ],
  templateUrl: './edit-joint-details.component.html',
  styleUrl: './edit-joint-details.component.scss',
})
export class EditJointDetailsComponent {
  isLoading$!: Observable<boolean>;
  subs: Subscription[] = [];
  jointAccountDataSubject$ = new BehaviorSubject<GetJointAccountData | null>(
    null
  );
  jointAccountData$ = this.jointAccountDataSubject$.asObservable();
  jointAccountId$ = this.jointAccountDataSubject$
    .asObservable()
    .pipe(map((data) => data?.joint_kyc?.[0]?.id));
  jointAccountName$ = this.jointAccountDataSubject$.asObservable().pipe(
    map((data) => {
      if (!data?.joint_kyc?.length) return 'Joint Account';
      return data.joint_kyc
        .map((profile) => {
          const firstName = profile.personal_details.first_name || '';
          const lastName = profile.personal_details.last_name || '';
          return `${firstName} ${lastName}`.trim();
        })
        .join(' & ');
    })
  );
  idKycDetails!: JointIdkyc[]; // PAIR for two users
  employmentDetails!: JointEmployerkyc;
  nextOfKinDetails!: JointNextofkin;
  locationDetails!: JointLocationkyc;
  personalDetails!: JointPersonaldetails[]; // PAIR for two users
  bankDetails!: JointAccountbankinfo[];
  documentDetails!: DocKyc[];
  signatoriesList: GetAccountSignatoriesData[] = [];
  jointDataDetails!: Jointkyc[];
  accountId!: string;

  private breadCrumbService = inject(BreadCrumbService);
  private route = inject(ActivatedRoute);
  private jointAccountService = inject(JointRegistrationService);
  private loadingService = inject(LoadingService);
  private corporateService = inject(CorporateRegistrationService);
  private toast = inject(NotificationFacade);

  constructor() {
    this.isLoading$ = this.loadingService.getLoadingObservable(
      URL_KEYS.JOINT.GET_JOINT_ACCOUNT_KYC
    );
  }

  ngOnInit(): void {
    this.accountId = this.route.snapshot.parent?.params['accountId'];
    console.log(this.accountId);
    this.breadCrumbService.setPageBreadCrumb(this.route);
    if (this.accountId) {
      this.getJointKycInfo();
      this.getAccountSignatories();
    }
  }

  getJointKycInfo() {
    const sub = this.jointAccountService
      .getJointKycInfo(this.accountId)
      .subscribe({
        next: (response) => {
          if (response.joint_kyc) {
            this.jointAccountDataSubject$.next(response);
            this.bankDetails = response.account_bank_info ?? [];
            this.documentDetails = response.doc;
            this.jointDataDetails = response.joint_kyc;

            // Dynamically collect details for all profiles (supports 2 or 3)
            this.idKycDetails = [];
            this.personalDetails = [];
            for (const profile of response.joint_kyc) {
              this.idKycDetails.push(profile.id_kyc);
              this.personalDetails.push(profile.personal_details);
            }

            // Use first profile for shared KYC sections
            if (response.joint_kyc[0]) {
              this.employmentDetails = response.joint_kyc[0].employer_kyc;
              this.nextOfKinDetails = response.joint_kyc[0].next_of_kin;
              this.locationDetails = response.joint_kyc[0].location_kyc;
            }
          }
        },
        error: (err) => {
          console.error(err);
        },
      });
    this.subs.push(sub);
  }

  getAccountSignatories(): void {
    const sub = this.corporateService
      .getAccountSignatories(this.accountId)
      .subscribe({
        next: (response) => {
          this.signatoriesList = response.data;
        },
        error: () => {
          this.toast.error('Failed to fetch signatories');
        },
      });

    this.subs.push(sub);
  }
}
